import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { ViToastService, ViToolbarComponent } from '@vi-iot/ui';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CodeScannerService } from '@services';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { CommonStandaloneModule } from '../../common-standalone.module';
import {
  CodeScannerDialogResponse,
  CodeScannerResponseType,
} from '../../services/code-scanner.model';

@Component({
  selector: 'app-code-scanner-dialog',
  standalone: true,
  templateUrl: './code-scanner-dialog.component.html',
  styleUrls: ['./code-scanner-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    forwardRef(() => CommonStandaloneModule),
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    forwardRef(() => ViToolbarComponent),
  ],
})
export class CodeScannerDialogComponent implements OnInit, OnDestroy {
  @ViewChild('video') public video: ElementRef;
  ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<CodeScannerDialogComponent, CodeScannerDialogResponse>,
    private viToastService: ViToastService,
    private translation: TranslateService,
    public codeScannerService: CodeScannerService
  ) {}

  ngOnInit(): void {
    this.initStream();
  }

  ngOnDestroy(): void {
    this.codeScannerService.endStream();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onBackClick(): void {
    this.dialogRef.close();
  }

  onManualInputClick(): void {
    this.dialogRef.close({
      type: CodeScannerResponseType.CODE_SCANNER_MANUAL_INPUT_REQUEST,
    });
  }

  private initStream(): void {
    this.codeScannerService
      .initStream()
      .pipe(
        tap((stream) => {
          this.video.nativeElement.srcObject = stream;
        }),
        switchMap(() => this.codeScannerService.scanOnVideo(this.video)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (code) => {
          this.onSuccess(code);
        },
        (_error) => {
          this.onError();
        }
      );
  }

  private onSuccess(code: string): void {
    this.dialogRef.close({
      type: CodeScannerResponseType.CODE_SCANNER_RESULT,
      value: code,
    });
  }

  private onError(): void {
    this.viToastService.error({
      message: this.translation.instant('+QR_CODE_SCANNER.ERROR.NOPERMISSIONS'),
    });
    this.codeScannerService.permissionException.next(true);
    this.dialogRef.close();
  }
}
